import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Heart } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import GlobalSearch from '@/components/GlobalSearch';
import DarkModeToggle from '@/components/DarkModeToggle';
import { useFavorites } from '@/contexts/FavoritesContext';
import useInferredCountry from '@/hooks/useInferredCountry';

const Navigation = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { favoritesCount } = useFavorites();
  const { mapPath } = useInferredCountry();

  const isActive = (path) => {
    if (path.startsWith('/map/')) return location.pathname.startsWith('/map');
    return location.pathname === path;
  };

  const navLinks = [
    { path: '/', label: 'Home', testId: 'nav-home' },
    { path: mapPath, label: 'Map', testId: 'nav-map' },
    { path: '/directory', label: 'Directory', testId: 'nav-directory' },
    { path: '/events', label: 'Events', testId: 'nav-events' },
    { path: '/submit', label: 'Get Listed', testId: 'nav-submit' },
    { path: '/how-to-accept', label: 'Accept Bitcoin', testId: 'nav-how-to' },
    { path: '/about', label: 'About', testId: 'nav-about' },
  ];

  return (
    <nav className="border-b border-border bg-background sticky top-0 z-[1001]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="logo-link">
            <img
              src="/logos/orangelabel-96.png"
              alt="Orange Label"
              className="h-7 sm:h-8 w-auto"
              data-testid="nav-logo"
            />
            <span className="font-heading font-bold text-base sm:text-lg text-foreground">Orange Label</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.testId}
                to={link.path}
                data-testid={link.testId}
                className={`px-3 lg:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-primary hover:bg-accent'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side: Search, Favorites, Dark Mode, Mobile Menu */}
          <div className="flex items-center gap-2">
            {/* Global Search */}
            <GlobalSearch />

            {/* Favorites Link */}
            <Link
              to="/favorites"
              className="relative p-2 rounded-lg hover:bg-muted transition-colors"
              data-testid="nav-favorites"
            >
              <Heart className={`w-5 h-5 ${favoritesCount > 0 ? 'text-red-500 fill-red-500' : 'text-muted-foreground'}`} />
              {favoritesCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {favoritesCount > 9 ? '9+' : favoritesCount}
                </span>
              )}
            </Link>

            {/* Dark Mode Toggle */}
            <DarkModeToggle />

            {/* Mobile Menu Button */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="md:hidden">
                <button
                  className="p-2 rounded-md hover:bg-accent transition-colors"
                  aria-label="Open menu"
                >
                  <Menu className="w-6 h-6 text-foreground" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[320px] pt-12 z-[1100]">
                <nav className="flex flex-col gap-1">
                  {navLinks.map(link => (
                    <SheetClose asChild key={link.testId}>
                      <Link
                        to={link.path}
                        data-testid={`mobile-${link.testId}`}
                        className={`px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                          isActive(link.path)
                            ? 'text-primary bg-primary/10'
                            : 'text-foreground hover:text-primary hover:bg-accent'
                        }`}
                        onClick={() => setOpen(false)}
                      >
                        {link.label}
                      </Link>
                    </SheetClose>
                  ))}

                  {/* Favorites in mobile menu */}
                  <SheetClose asChild>
                    <Link
                      to="/favorites"
                      data-testid="mobile-nav-favorites"
                      className={`px-4 py-3 rounded-lg text-base font-medium transition-colors flex items-center gap-2 ${
                        isActive('/favorites')
                          ? 'text-primary bg-primary/10'
                          : 'text-foreground hover:text-primary hover:bg-accent'
                      }`}
                      onClick={() => setOpen(false)}
                    >
                      <Heart className={`w-5 h-5 ${favoritesCount > 0 ? 'text-red-500 fill-red-500' : ''}`} />
                      Favorites {favoritesCount > 0 && `(${favoritesCount})`}
                    </Link>
                  </SheetClose>
                </nav>

                {/* Mobile Footer */}
                <div className="absolute bottom-8 left-6 right-6">
                  <div className="flex items-center gap-2 mb-4">
                    <img
                      src="/logos/orangelabel-96.png"
                      alt="Orange Label"
                      className="h-6 w-auto"
                    />
                    <span className="text-sm text-muted-foreground">Orange Label</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    A global directory of businesses that accept Bitcoin.
                  </p>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
